import React, { useEffect, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { ContextLocale } from "../../context/ContextLocale";
import { link_to_details_wrapper } from "../../styles/modules/utilities.module.css";
import { darkOrLightColor } from "../../helpers/darkOrLightColor";
import {
  dialog_modal,
  modal_wrapper,
  modal_header,
  modal_header_left,
  modal_color_preview,
  modal_color_info,
  modal_color_hex,
  modal_header_actions,
  modal_body,
  modal_close,
  modal_copy_btn,
  modal_toast,
} from "./modal.module.css";

// Textos multiidioma
const TEXTS = {
  es: {
    close: "Cerrar",
    copy: "Copiar",
    copied: "¡Copiado!",
    explore: "Explorar color",
  },
  en: {
    close: "Close",
    copy: "Copy",
    copied: "Copied!",
    explore: "Explore color",
  },
  pt: {
    close: "Fechar",
    copy: "Copiar",
    copied: "Copiado!",
    explore: "Explorar cor",
  },
  fr: {
    close: "Fermer",
    copy: "Copier",
    copied: "Copié!",
    explore: "Explorer la couleur",
  },
  de: {
    close: "Schließen",
    copy: "Kopieren",
    copied: "Kopiert!",
    explore: "Farbe erkunden",
  },
};

// Componente del Modal que se renderiza con Portal
function ModalContent({ children, color, onClose, lang }) {
  const texts = TEXTS[lang] || TEXTS.es;
  const [showToast, setShowToast] = useState(false);
  const colorHex = color?.toUpperCase() || "#000000";

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Manejar tecla Escape
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Copiar color
  const copyColor = useCallback(() => {
    navigator.clipboard.writeText(colorHex);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, [colorHex]);

  // Click en el backdrop cierra el modal
  const handleBackdropClick = useCallback(
    e => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  return createPortal(
    <div className={dialog_modal} onClick={handleBackdropClick}>
      {/* Header fijo */}
      <header className={modal_header}>
        <div className={modal_header_left}>
          <div
            className={modal_color_preview}
            style={{ backgroundColor: colorHex }}
          />
          <div className={modal_color_info}>
            <span className={modal_color_hex}>{colorHex}</span>
          </div>
        </div>

        <div className={modal_header_actions}>
          <button className={modal_copy_btn} onClick={copyColor}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            <span>{texts.copy}</span>
          </button>
          <button className={modal_close} onClick={onClose}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span>{texts.close}</span>
          </button>
        </div>
      </header>

      {/* Contenido scrolleable */}
      <div className={modal_wrapper}>
        <div className={modal_body} onClick={e => e.stopPropagation()}>
          {children}
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className={modal_toast}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {texts.copied} {colorHex}
        </div>
      )}
    </div>,
    document.body
  );
}

function Modal({ children, color }) {
  const { lang } = useContext(ContextLocale);
  const texts = TEXTS[lang] || TEXTS.es;
  const [isOpen, setIsOpen] = useState(false);
  const textColor = darkOrLightColor(color);

  const openModal = useCallback(() => {
    setIsOpen(true);

    // Google Ads reward slot (manteniendo la funcionalidad existente)
    if (typeof window !== "undefined") {
      const hasConsent = (() => {
        try {
          const storedConsent = window.localStorage.getItem("cookiesConsent");
          return storedConsent === null ? true : storedConsent === "true";
        } catch (error) {
          return true;
        }
      })();

      if (!hasConsent) {
        return;
      }

      const googletag = (window.googletag = window.googletag || { cmd: [] });

      googletag.cmd.push(function () {
        var rewardSlot = googletag.defineOutOfPageSlot(
          "/22250597679/BloquesWortise/BloquesWortise_Reward",
          googletag.enums.OutOfPageFormat.REWARDED
        );
        if (rewardSlot) {
          rewardSlot.addService(googletag.pubads());
          googletag
            .pubads()
            .addEventListener("rewardedSlotReady", function (event) {
              if (event.slot === rewardSlot) {
                event.makeRewardedVisible();
              }
            });
          if (!window.__ptdcGoogletagServicesEnabled) {
            googletag.enableServices();
            window.__ptdcGoogletagServicesEnabled = true;
          }
          googletag.display(rewardSlot);
        }
      });
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      {/* Botón para abrir el modal */}
      <button
        className={link_to_details_wrapper}
        onClick={openModal}
        title={texts.explore}
        style={{
          boxShadow: `0px 0px 3px 0px ${textColor}`,
          color: textColor,
          background: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          border: "none",
          padding: "0.5rem",
          margin: "0",
          cursor: "pointer",
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={textColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
      </button>

      {/* Modal renderizado con Portal (fuera del DOM del padre) */}
      {isOpen && (
        <ModalContent color={color} onClose={closeModal} lang={lang}>
          {children}
        </ModalContent>
      )}
    </>
  );
}

export default Modal;
